import {
  AuthenticatedAppGate,
  AuthProvider,
  createLoginMethodLookup,
  type FetchUserData,
  useAuth,
  type ValidateAuthenticatedUser,
} from "@assetwatch/auth";
import {
  Alert,
  AssetWatchUiProvider,
  Container,
  FullPageLoading,
  Stack,
  Text,
  Title,
} from "@assetwatch/ui";
import * as Sentry from "@sentry/react";
import { type QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRootRouteWithContext, Outlet } from "@tanstack/react-router";
import { post } from "aws-amplify/api";
import { type ReactNode, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { EnterpriseApiError } from "../api/enterpriseClient";
import { EnterpriseCustomerAccessCard } from "../components/EnterpriseCustomerState";
import { EnterpriseFeatureGate } from "../components/EnterpriseFeatureGate";
import { EnterprisePendoProvider } from "../components/EnterprisePendoProvider";
import LaunchDarklyProvider from "../components/LaunchDarkly/LaunchDarklyProvider";
import config from "../config";
import { useLanguageSync } from "../hooks/useLanguageSync";
import i18n from "../i18n/config";
import { buildEnterpriseCustomLoginFormMessages } from "../i18n/enterpriseMessageBuilders";

const authApiBaseUrl =
  (import.meta.env.VITE_AUTH_API_BASE_URL as string | undefined)?.replace(
    /\/$/,
    "",
  ) ?? "";
const hasProvisioningApi = authApiBaseUrl.length > 0;

type CurrentUserInfo = {
  email?: string;
  roles?: unknown;
  preferences?: unknown[];
};

const signOutRedirect = {
  clientId: config.cognito.APP_CLIENT_ID,
  domain: config.cognito.DOMAIN,
  redirectUri: `${window.location.origin}/`,
};

async function getCurrentEnterpriseUserInfo() {
  const { body } = await post({
    apiName: "apiVeroUser",
    path: "/list",
    options: {
      body: {
        meth: "getCurrentUserInfo",
      },
    },
  }).response;

  return (await body.json()) as CurrentUserInfo;
}

function getUserRoles(value: unknown) {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter((role): role is string => typeof role === "string");
}

const validateAuthenticatedUser: ValidateAuthenticatedUser | undefined =
  hasProvisioningApi
    ? async ({ user }) => {
        if (!user.email) {
          return {
            ok: false,
            message: i18n.t(
              "enterprise.auth.signIn.errors.userNotFoundSupport",
              { userId: user.sub },
            ),
          };
        }

        return { ok: true };
      }
    : undefined;

const fetchEnterpriseUserData: FetchUserData | undefined = hasProvisioningApi
  ? async () => {
      const userInfo = await getCurrentEnterpriseUserInfo();

      return {
        email: userInfo.email ?? "",
        roles: getUserRoles(userInfo.roles),
        preferences: Array.isArray(userInfo.preferences)
          ? userInfo.preferences
          : [],
      };
    }
  : undefined;

const lookupLoginMethod = createLoginMethodLookup(authApiBaseUrl);

function AuthGate({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  return (
    <AuthenticatedAppGate
      loadingFallback={<FullPageLoading />}
      loginMessages={buildEnterpriseCustomLoginFormMessages(t)}
      lookupLoginMethod={lookupLoginMethod}
      supportHref="mailto:support@assetwatch.com"
    >
      {children}
    </AuthenticatedAppGate>
  );
}

function SentryUserScopeSync() {
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      Sentry.setUser({ id: user.sub, email: user.email });
      return;
    }

    Sentry.setUser(null);
  }, [user]);

  return null;
}

function LanguagePreferenceSync() {
  useLanguageSync();
  return null;
}

function RootProviders() {
  return (
    <QueryClientProvider client={Route.useRouteContext().queryClient}>
      <AuthProvider
        fetchUserData={fetchEnterpriseUserData}
        validateAuthenticatedUser={validateAuthenticatedUser}
        signOutRedirect={signOutRedirect}
      >
        <LaunchDarklyProvider>
          <LanguagePreferenceSync />
          <EnterprisePendoProvider />
          <SentryUserScopeSync />
          <AssetWatchUiProvider
            defaultTheme="light"
            storageKey="assetwatch-enterprise-theme"
          >
            <AuthGate>
              <LanguagePreferenceSync />
              <EnterpriseFeatureGate>
                <Outlet />
              </EnterpriseFeatureGate>
            </AuthGate>
          </AssetWatchUiProvider>
        </LaunchDarklyProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

function RootError({ error }: { error: Error }) {
  const { t } = useTranslation();

  if (
    error instanceof EnterpriseApiError &&
    (error.status === 403 || error.status === 404)
  ) {
    return (
      <div className="p-(--spacing-40)">
        <EnterpriseCustomerAccessCard />
      </div>
    );
  }

  if (
    error instanceof EnterpriseApiError &&
    error.status >= 500 &&
    error.operation === "customer-context"
  ) {
    return (
      <Container size="sm">
        <Stack gap={16}>
          <Title size="lg">
            {t("enterprise.errors.root.customerUnavailableTitle")}
          </Title>
          <Alert
            tone="negative"
            title={t("enterprise.errors.root.customerUnavailableDescription")}
          />
        </Stack>
      </Container>
    );
  }

  return (
    <Container size="sm">
      <Stack gap={16}>
        <Title size="lg">
          {t("enterprise.errors.root.enterpriseUnavailableTitle")}
        </Title>
        <Alert tone="negative" title={error.message} />
      </Stack>
    </Container>
  );
}

function RootNotFound() {
  const { t } = useTranslation();

  return (
    <Container size="sm">
      <Stack gap={16}>
        <Title size="lg">{t("enterprise.errors.root.notFoundTitle")}</Title>
        <Text tone="subtle">
          {t("enterprise.errors.root.notFoundDescription")}
        </Text>
      </Stack>
    </Container>
  );
}

export const Route = createRootRouteWithContext<{
  queryClient: QueryClient;
}>()({
  component: RootProviders,
  errorComponent: RootError,
  notFoundComponent: RootNotFound,
  pendingComponent: FullPageLoading,
});
